import { each, forIn, get, includes, mapValues, set, uniqBy, isEmpty } from "lodash";
import validate from "validate.js";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints, validateRequiredDocuments } from "../../utils/validation";

const requestListingFromExisting = {
    propertyKey: {
        ...constraints.required,
    },
};

const listingBrokerValidationSchema = {
    "brokerAgentKey.person.firstName": {},
    "brokerAgentKey.person.lastName": {},
    "company.displayName": {
        ...constraints.required,
    },
};

const mixedUseContraints = {
    sf: {
        ...constraints.number,
    },
    unitCount: {
        ...constraints.id,
    },
};

const portfolioConstraints = {
    propertyId: {
        ...constraints.id,
    },
};

const condoConstraints = {
    condoNumber: {},
    condoSf: {
        ...constraints.number,
    },
    condoParcelId: {},
};

export const requestListingFromNew = {
    newPropertyName: {
        ...constraints.required,
    },
    newPropertyAddress: {
        ...constraints.required,
    },
    newPropertyCity: {
        ...constraints.required,
    },
    newPropertyState: {
        ...constraints.required,
    },
    newPropertyZip: {
        ...constraints.required,
        ...constraints.id,
    },
};

export const requestListingNonRequiredConstraints = {
    newPropertyZip: {
        ...constraints.id,
    },
};

const listingForm = {
    dateListed: {
        ...constraints.date,
        ...constraints.required,
    },
    totalUnits: {
        ...constraints.number,
    },
    avgPsfMonthlyRate: {
        ...constraints.number,
    },
    avgPsfAnnualRate: {
        ...constraints.number,
    },
    lotAcres: {
        ...constraints.number,
    },
    lotSf: {
        ...constraints.number,
    },
    listPrice: {},
};

const listingFormAttributes = {
    useType: {
        ...constraints.required,
    },
};

const isDivisibleConstraints = {
    sfDivisibleTo: {
        ...constraints.required,
        ...constraints.number,
    },
};

const saleListingForm = {};

const leaseListingForm = {
    "attributes.rentType": {
        ...constraints.required,
    },
};

const listingExpirationConstraint = {
    expirationDate: {
        ...constraints.date,
        ...constraints.required,
    },
};

export const listingConstraints = {
    Office: {
        //verified
        "attributes.suite": {},
        "attributes.sfAvail": {},
        "attributes.largestContigSf": {},
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Industrial: {
        "attributes.sfAvail": {
            ...constraints.id,
        },
        "attributes.largestContigSf": {},
        "attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "attributes.blendedRate": {
            ...constraints.number,
        },
        "attributes.voltsNum": {},
        "attributes.ampsNum": {
            ...constraints.number,
        },
        "attributes.phaseNum": {},
        "attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "attributes.craneNotes": {},
        "attributes.yardNotes": {},
        "attributes.specialtyType": {
            ...constraints.required,
        },
        "attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    "Life Science": {
        //verified
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Retail: {
        //verified
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Land: {
        //verified
        "attributes.sfAvail": {
            ...constraints.id,
        },
        "attributes.totalAcres": {
            ...constraints.number,
        },
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Multifamily: {
        //verified
        "attributes.multifamilyUnitCount": {
            ...constraints.id,
        },
        "attributes.multifamilySf": {
            ...constraints.number,
        },
        "attributes.unitMix": {},
        "attributes.wetlandsPct": {
            ...constraints.number,
        },
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Hospitality: {
        //listing-specific
        "attributes.hospitalitySf": {
            ...constraints.id,
        },
        "attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "attributes.roomCount": {
            ...constraints.id,
        },
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Other: {
        "attributes.sfAvail": {},
        "attributes.largestContigSf": {},
        "attributes.suite": {},
        "attributes.secondarySfAvailable": {},
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Educational: {
        "attributes.classroomCount": {
            ...constraints.wholeNumber,
        },
        "attributes.auditoriumSeatsCount": {
            ...constraints.wholeNumber,
        },
    },
    Religious: {},
    Healthcare: {
        //verified
        "attributes.suite": {},
        "attributes.sfAvail": {},
        "attributes.largestContigSf": {},
        "attributes.specialtyType": {
            ...constraints.required,
        },
    },
    listingInformation: {
        //building information?
        listingName: {
            ...constraints.required,
        },
        dateListed: {
            ...constraints.date,
        },
        dateAvailable: {
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.date,
        },
        leaseExpirationDate: {
            ...constraints.date,
        },
        "attributes.totalUnits": {
            ...constraints.number,
        },
        "attributes.numBldg": {
            ...constraints.number,
        },
        "attributes.lotAcres": {
            ...constraints.number,
        },
        "attributes.lotSf": {
            ...constraints.number,
        },
        "attributes.amenities": {},
        "attributes.tourInstructions": {},
        sfAvail: {
            ...constraints.required,
            ...constraints.number,
            ...constraints.positiveNumber,
        },
        sellerLandlordCommonCompanyName: {
            ...constraints.required,
            ...constraints.maxLength,
        },
        sellerLandlordLegalCompanyName: {
            ...constraints.required,
            ...constraints.maxLength,
        },
    },
    parkingInformation: {
        "attributes.parkingTotal": {
            ...constraints.number,
        },
        "attributes.parkingRatio": {},
        "attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
        "attributes.parkingNotes": {},
    },
    listingFinancial: {
        leaseType: {
            ...constraints.required,
        },
        improvementAllowance: {
            ...constraints.number,
        },
        askingRentMax: {
            ...constraints.required,
            ...constraints.number,
        },
        askingRentPerMonth: {
            ...constraints.required,
            ...constraints.number,
        },
        baseRentPerYear: {
            ...constraints.required,
            ...constraints.number,
        },
        expenseType: {},
        expenseTypeValue: {},
        nnnPerMonth: {
            ...constraints.number,
        },
        nnnPerYear: {
            ...constraints.number,
        },
        totalMonthlyRent: {
            ...constraints.number,
        },
        totalRentPerYear: {
            ...constraints.number,
        },
        procuringBrokerCommission: {},
    },
    Lease: {
        //general information
        //required fields
    },
    Sale: {
        //required fields
        listPrice: {
            ...constraints.required,
        },
    },
};

const validateRequestListingFromExisting = (values) => {
    return validate(values, requestListingFromExisting, { fullMessages: false });
};

const validateRequestListingFromNew = (values) => {
    return validate(values, requestListingFromNew, { fullMessages: false });
};

const validateListingForm = (values) => {
    return validate(values, listingForm, { fullMessages: false });
};

const validateListingFormAttributes = (values) => {
    return validate(values, listingFormAttributes, { fullMessages: false });
};

const validateSaleListingForm = (values) => {
    return validate(values, saleListingForm, { fullMessages: false });
};

const validateLeaseListingForm = (values) => {
    return validate(values, leaseListingForm, { fullMessages: false });
};

const validateListingBroker = (listingBroker) => {
    return validate(listingBroker, listingBrokerValidationSchema, { fullMessages: false });
};

const validateMixedUse = (use) => {
    return validate(use, mixedUseContraints, { fullMessages: false });
};

const validatePortfolio = (portfolio) => {
    return validate(portfolio, portfolioConstraints, { fullMessages: false });
};

const validateCondo = (condo) => {
    return validate(condo, condoConstraints, { fullMessages: false });
};

const validateisDivisible = (isDivisible, sfDivisibleTo) => {
    if (isDivisible) {
        const errors = validateSingleValue("sfDivisibleTo", sfDivisibleTo, isDivisibleConstraints);
        return isEmpty(errors) ? {} : { sfDivisibleTo: true };
    }
};

const validateSingleValue = (field, value, constraint) => {
    return validate({ [field]: value }, constraint, { fullMessages: false });
};

const validateListingBrokers = (listing) => {
    let kmBrokerErrors = {};
    let outsideBrokerErrors = {};
    let listingBrokers = get(listing, "listingBrokers", {});
    if (listing.nonKmListingFlg) {
        let outsideListingBrokers = get(listingBrokers, "outsideListingBrokers", []);
        each(outsideListingBrokers, (l) => {
            if (!get(l, "brokerAgentKey.person.firstName")) {
                set(outsideBrokerErrors, `${l.uniqueId}.firstName`, true);
            }
            if (!get(l, "brokerAgentKey.person.lastName")) {
                set(outsideBrokerErrors, `${l.uniqueId}.lastName`, true);
            }
            if (!get(l, "company.displayName", null)) {
                set(outsideBrokerErrors, `${l.uniqueId}.company`, true);
            }
        });
    } else {
        let kmListingBrokers = get(listingBrokers, "kmListingBrokers", []);
        let outsideListingBrokers = get(listingBrokers, "outsideListingBrokers", []);
        each(kmListingBrokers, (l, i) => {
            let brokerAgentKey = get(l, "brokerAgentKey.brokerAgentKey", null);
            if (!brokerAgentKey) {
                set(kmBrokerErrors, l.uniqueId, true);
            }
        });
        each(outsideListingBrokers, (l, i) => {
            if (
                !get(l, "company.displayName") &&
                (get(l, "brokerAgentKey.person.firstName") || get(l, "brokerAgentKey.person.lastName"))
            ) {
                set(outsideBrokerErrors, `${l.uniqueId}.company`, true);
            }
            if (
                !get(l, "brokerAgentKey.person.firstName") &&
                (get(l, "brokerAgentKey.person.lastName") || get(l, "company.displayName"))
            ) {
                set(outsideBrokerErrors, `${l.uniqueId}.firstName`, true);
            }
            if (
                !get(l, "brokerAgentKey.person.lastName") &&
                (get(l, "brokerAgentKey.person.firstName") || get(l, "company.displayName"))
            ) {
                set(outsideBrokerErrors, `${l.uniqueId}.lastName`, true);
            }
        });
    }
    return isEmpty(kmBrokerErrors)
        ? isEmpty(outsideBrokerErrors)
            ? {}
            : { outsideBrokerErrors: outsideBrokerErrors }
        : { kmBrokerErrors: kmBrokerErrors, outsideBrokerErrors: outsideBrokerErrors };
};

const validateListingBrokerByIndex = (listing, uniqueId, isOutside) => {
    const listingBrokers = listing?.listingBrokers ?? {};

    if (isOutside) {
        const outsideListingBrokers = listingBrokers?.outsideListingBrokers ?? [];
        const broker = outsideListingBrokers.find((b) => b?.uniqueId === uniqueId);
        if (!broker) return [];

        const firstName = broker?.brokerAgentKey?.person?.firstName;
        const lastName = broker?.brokerAgentKey?.person?.lastName;
        const company = broker?.company?.displayName;

        const base = `outsideBrokerErrors.${uniqueId}`;

        if (listing.nonKmListingFlg) {
            return [
                { fullKey: `${base}.firstName`, resolved: !!firstName },
                { fullKey: `${base}.lastName`, resolved: !!lastName },
                { fullKey: `${base}.company`, resolved: !!company },
            ];
        }

        return [
            { fullKey: `${base}.company`, resolved: !(!company && (firstName || lastName)) },
            { fullKey: `${base}.firstName`, resolved: !(!firstName && (lastName || company)) },
            { fullKey: `${base}.lastName`, resolved: !(!lastName && (firstName || company)) },
        ];
    }

    const kmListingBrokers = listingBrokers?.kmListingBrokers ?? [];
    const broker = kmListingBrokers.find((b) => b?.uniqueId === uniqueId);
    if (!broker) return [];

    const brokerAgentKey = broker?.brokerAgentKey?.brokerAgentKey ?? null;
    return [{ fullKey: `kmBrokerErrors.${uniqueId}`, resolved: !!brokerAgentKey }];
};

const validateListingBrokersWhenCombined = (listingBrokers) => {
    if (!listingBrokers) {
        return { "listingBrokers.eitherLogic": ["One of either km broker or outside broker must be present"] };
    } else {
        let listingBrokerErrors = {};
        let kmEmpty = true;
        let outsideEmpty = true;
        let counter = 0;
        each(listingBrokers, (l, i) => {
            if (get(l, "brokerAgentKey.kmBrokerFlag")) {
                if (get(l, "brokerAgentKey.brokerAgentKey")) {
                    kmEmpty = false;
                }
            } else {
                if (
                    get(l, "brokerAgentKey.person.firstName") ||
                    get(l, "brokerAgentKey.person.lastName") ||
                    get(l, "company.displayName")
                ) {
                    outsideEmpty = false;
                    const validationResponse = validateListingBroker(l);
                    if (isEmpty(validationResponse)) {
                        return;
                    } else {
                        set(listingBrokerErrors, `outside.${counter}`, {
                            ...mapValues(validationResponse, (value, key) => {
                                return value[0];
                            }),
                        });
                    }
                }
                counter += 1;
            }
        });
        if (kmEmpty && outsideEmpty) {
            set(listingBrokerErrors, `eitherLogic`, "One of either km broker or outside broker must be present");
        }
        return isEmpty(listingBrokerErrors) ? {} : { "listing.listingBrokers": listingBrokerErrors };
    }
};

const validateListingBrokersCompany = (listingBrokers) => {
    let listingBrokerErrors = {};
    let counter = 0;
    if (listingBrokers) {
        for (let i = 0; i < listingBrokers.length; i++) {
            const lb = listingBrokers[i];
            if (!get(lb, "brokerAgentKey.kmBrokerFlag")) {
                if (get(lb, "brokerAgentKey.person.firstName") || get(lb, "brokerAgentKey.person.lastName")) {
                    const validationResponse = validateListingBroker(lb);
                    if (!isEmpty(validationResponse)) {
                        set(listingBrokerErrors, `outside.${counter}`, {
                            ...mapValues(validationResponse, (value, key) => {
                                return value[0];
                            }),
                        });
                    }
                }
                counter++;
            }
        }
    }
    return isEmpty(listingBrokerErrors) ? {} : { "listing.listingBrokers": listingBrokerErrors };
};

const validateMixedUses = (mixedUses) => {
    let mixedUseErrors = {};
    let counter = 0;
    each(mixedUses, (m, i) => {
        const validationResponse = validateMixedUse(m);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(mixedUseErrors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(mixedUseErrors) ? {} : { "attributes.mixedUses": mixedUseErrors };
};

const validatePortfolioProperties = (properties) => {
    let errors = {};
    let counter = 0;
    each(properties, (p, i) => {
        const validationResponse = validatePortfolio(p);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(errors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(errors) ? {} : { "attributes.portfolioProperties": errors };
};

const validateCondos = (condos) => {
    let errors = {};
    let counter = 0;
    each(condos, (c, i) => {
        const validationResponse = validateCondo(c);
        if (isEmpty(validationResponse)) {
            return;
        } else {
            set(errors, `${counter}`, {
                ...mapValues(validationResponse, (value, key) => {
                    return value[0];
                }),
            });
        }
        counter += 1;
    });
    return isEmpty(errors) ? {} : { "attributes.condos": errors };
};

const validateAutoRenew = (listing) => {
    if (get(listing, "autoRenewFlg") !== true) {
        const errors = validateSingleValue(
            "expirationDate",
            get(listing, "expirationDate"),
            listingExpirationConstraint
        );
        return isEmpty(errors) ? {} : { expirationDate: true };
    }
    return {};
};

const validateListing = (listing, useType, transactionType, documentState) => {
    let whatToValidate = [];

    if (transactionType === "Lease") {
        whatToValidate = [useType, transactionType, "listingInformation", "parkingInformation", "listingFinancial"];
    } else if (transactionType === "Sale") {
        whatToValidate = [useType, transactionType, "listingInformation", "parkingInformation"];
    }

    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...listingConstraints[section] };
    });
    let setErrors = {};
    let errors = {
        ...mapValues(validate(listing, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...validateListingBrokers(listing),
        ...validateMixedUses(get(listing, "attributes.mixedUses")),
        ...validatePortfolioProperties(get(listing, "attributes.portfolioProperties")),
        ...validateCondos(get(listing, "attributes.condos")),
        ...validateAutoRenew(listing),
        ...validateisDivisible(get(listing, "isDivisible"), get(listing, "sfDivisibleTo")),
    };

    if (!get(listing, "nonKmListingFlg")) {
        errors = {
            ...errors,
            ...validateRequiredDocuments(documentState, "listingForm"),
        };
    }

    forIn(errors, (value, key) => {
        set(setErrors, key, value);
    });

    return isEmpty(setErrors) ? null : setErrors;
};

const validateListingByField = (section, id, value, listing, options = {}) => {
    switch (section) {
        case "listingFinancial": {
            const sectionConstraints = listingConstraints.listingFinancial;
            const sectionErrors = validate(listing, sectionConstraints, { fullMessages: false }) || {};
            return Object.keys(sectionConstraints).map((fullKey) => ({
                fullKey,
                resolved: isEmpty(sectionErrors[fullKey]),
            }));
        }
        case "listingBrokers": {
            return validateListingBrokerByIndex(listing, options.uniqueId, options.isOutside);
        }
        case "mixedUse": {
            const { fullKey, result } = validateFieldWithKey(mixedUseContraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        case "condo": {
            const { fullKey, result } = validateFieldWithKey(condoConstraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        case "portfolio": {
            const { fullKey, result } = validateFieldWithKey(portfolioConstraints, id, value);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
        case "sfDivisibleTo": {
            const result = validateisDivisible(listing?.isDivisible, listing?.sfDivisibleTo);
            return [{ fullKey: "sfDivisibleTo", resolved: isEmpty(result) }];
        }
        default: {
            const { fullKey, result } = validateFieldWithKey(listingConstraints, id, value, section);
            return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
        }
    }
};

const getPanelsToExpand = (errors) => {
    let listingInformationConstraints = {};
    each(["useType", "transactionType", "listingInformation", "parkingInformation"], (section) => {
        listingInformationConstraints = { ...listingInformationConstraints, ...listingConstraints[section] };
    });

    let panelsToExpand = [];
    forIn(errors, (value, key) => {
        if (includes(Object.keys(listingConstraints.listingFinancial), key)) {
            panelsToExpand.push("listingFinancial");
        } else if (includes(Object.keys(listingInformationConstraints), key)) {
            panelsToExpand.push("listingInformation");
        }
    });

    panelsToExpand = uniqBy(panelsToExpand, function (e) {
        return e.id;
    });

    return panelsToExpand;
};

export default {
    validateRequestListingFromExisting,
    validateRequestListingFromNew,
    validateListingForm,
    validateSaleListingForm,
    validateLeaseListingForm,
    validateListingFormAttributes,
    validateListingBrokers,
    validateListingBrokersWhenCombined,
    validateAutoRenew,
    validateMixedUses,
    validatePortfolioProperties,
    validateCondos,
    validateisDivisible,
    validateListing,
    validateListingByField,
    getPanelsToExpand,
    validateListingBrokersCompany,
};
